import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Tab, Skeleton } from '@mui/material';
import { Box, Typography, IconButton, Tooltip, Chip, Button } from 'components/mui';
import { EditIcon, CopyIcon } from 'components/icons';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useCaseDetail from 'containers/pages/cases/hooks/useCaseDetail';
import CaseGeneralInfo from './CaseGeneralInfo';
import { CaseComments } from 'containers/pages/cases/components/caseComments';
import { CaseAttachments } from 'containers/pages/cases/components/caseAttachments';
import { CaseSummary } from 'containers/pages/cases/components/caseSummary';
import { CaseFormDialog } from 'containers/pages/cases/components/caseForm';
import CaseActivityLog from './CaseActivityLog';
import { ROUTE_CASES } from 'constants/routerPath';
import { getStatusColor } from 'containers/pages/cases/utils/getStatusColor';

function CaseDetailPage() {
  const { caseId: caseIdParam } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const caseId = caseIdParam ? Number(caseIdParam) : null;

  const { state, handler } = useCaseDetail();
  const { caseData, loading, activeTab } = state;

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formDialogMode, setFormDialogMode] = useState<'add' | 'edit' | 'copy'>('edit');

  useEffect(() => {
    if (caseId !== null && !isNaN(caseId)) {
      handler.fetchCase(caseId);
    }
  }, [caseId, handler.fetchCase]);

  const tabs = useMemo(() => {
    const items = [
      { label: 'General', value: 0 },
      { label: 'Comments', value: 1 },
      { label: 'Attachments', value: 2 },
    ];
    if (caseData?.HasSecureAttachments === 'true') {
      items.push({ label: 'Secure Attachments', value: items.length });
    }
    items.push({ label: 'Activity Log', value: items.length });
    items.push({ label: 'Summary', value: items.length });
    return items;
  }, [caseData?.HasSecureAttachments]);

  const hasSecure = caseData?.HasSecureAttachments === 'true';
  const secureTabIndex = 3;
  const activityLogTabIndex = hasSecure ? 4 : 3;
  const summaryTabIndex = hasSecure ? 5 : 4;

  const handleEdit = () => {
    setFormDialogMode('edit');
    setFormDialogOpen(true);
  };

  const handleDuplicate = () => {
    setFormDialogMode('copy');
    setFormDialogOpen(true);
  };

  const handleFormSave = () => {
    handler.refetch();
  };

  if (caseId === null || isNaN(caseId)) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Invalid case ID</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate(ROUTE_CASES)}>
          Back to Cases
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tooltip title="Back to Cases">
          <IconButton onClick={() => navigate(ROUTE_CASES)}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        {loading ? (
          <Skeleton variant="text" width={300} height={40} />
        ) : caseData ? (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h5" component="h1" noWrap>
                  {caseData.Title}
                </Typography>
                <Chip
                  label={caseData.StatusTitle}
                  size="small"
                  color={getStatusColor(caseData.StatusTitle)}
                  sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 1 }}
                />
              </Box>
              {caseData.StatusComment && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {caseData.StatusComment}
                </Typography>
              )}
            </Box>
            <Tooltip title="Edit Case">
              <IconButton onClick={handleEdit}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate Case">
              <IconButton onClick={handleDuplicate}>
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : state.error ? (
          <Typography color="error">{state.error}</Typography>
        ) : null}
      </Box>

      {/* Tabs */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={200} />
          <Skeleton variant="rounded" height={150} />
        </Box>
      ) : caseData ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              data-tour="case-tabs"
              value={activeTab}
              onChange={(_e, v) => handler.setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} />
              ))}
            </Tabs>
          </Box>
          <Box sx={{ flex: 1 }}>
            {activeTab === 0 && <CaseGeneralInfo caseData={caseData} onReferenceClick={(refId) => navigate(`${ROUTE_CASES}/${refId}`)} />}
            {activeTab === 1 && (
              <Box sx={{ py: 2 }}>
                <CaseComments
                  caseId={caseData.Id}
                  caseTitle={caseData.Title}
                  customerId={caseData.CustomerId}
                  customerName={caseData.CustomerName}
                  groupId={caseData.GroupId}
                  groupTitle={caseData.GroupTitle}
                  projectId={caseData.ProjectId}
                  projectTitle={caseData.ProjectTitle}
                  deliverableId={caseData.DeliverableId}
                  deliverableTitle={caseData.DeliverableTitle}
                />
              </Box>
            )}
            {activeTab === 2 && (
              <Box sx={{ py: 2 }}>
                <CaseAttachments caseId={caseData.Id} customerId={caseData.CustomerId} />
              </Box>
            )}
            {caseData.HasSecureAttachments === 'true' && activeTab === secureTabIndex && (
              <Box sx={{ py: 2 }}>
                <CaseAttachments caseId={caseData.Id} customerId={caseData.CustomerId} secure />
              </Box>
            )}
            {activeTab === activityLogTabIndex && (
              <Box sx={{ py: 2 }}>
                <CaseActivityLog
                  caseId={caseData.Id}
                  createdOn={caseData.CreatedOn}
                  updatedOn={caseData.UpdatedOn}
                />
              </Box>
            )}
            {activeTab === summaryTabIndex && (
              <Box sx={{ py: 2 }}>
                <CaseSummary caseData={caseData} />
              </Box>
            )}
          </Box>
        </>
      ) : null}

      {/* Edit/Duplicate Dialog */}
      <CaseFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        mode={formDialogMode}
        caseId={caseId}
        onSave={handleFormSave}
      />
    </Box>
  );
}

export default CaseDetailPage;
